import type { ReactNode } from 'react';
import { Container } from '@vnetwork/ui/layout';
import { Card, CardHeader, CardTitle, CardBody } from '@vnetwork/ui/primitives';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { DetailHeader } from '@/components/detail-header';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

export interface MasterDetailScaffoldProps {
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** True once the record is available. */
  loaded: boolean;
  /** Back-to-list route + label. */
  backTo: string;
  backLabel: string;
  /** Muted `#code`-style prefix beside the title. */
  codePrefix?: ReactNode;
  title: string;
  /** Status pill / badges rendered next to the title. */
  status?: ReactNode;
  /** Right-aligned header actions (e.g. an Edit button). */
  actions?: ReactNode;
  /** Info-card section title. */
  cardTitle: string;
  /** Label/value pairs for the primary info card. */
  items: InfoItem[];
  /** Optional extra sections rendered below the info card (e.g. specs). */
  children?: ReactNode;
}

/**
 * Read-only master detail scaffold — a back-to-list Button, an identity header
 * (`#code` + name + status pill), then a primary info Card (`InfoGrid`) and any
 * extra sections. Handles loading / error / empty via the shared tab-state
 * components. Shared by the Supplier, Model and Location detail pages.
 */
export function MasterDetailScaffold({
  isLoading,
  isError,
  onRetry,
  loaded,
  backTo,
  backLabel,
  codePrefix,
  title,
  status,
  actions,
  cardTitle,
  items,
  children,
}: MasterDetailScaffoldProps) {
  return (
    <Container size="full">
      {isError ? (
        <TabError title={`Failed to load ${backLabel.toLowerCase()}`} onRetry={onRetry} />
      ) : isLoading || !loaded ? (
        <TabLoading rows={4} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <DetailHeader
            parent={[
              { label: 'Capacities & Resources', to: '/capacities' },
              { label: backLabel, to: backTo },
            ]}
            current={title}
            idPrefix={codePrefix}
            title={title}
            meta={status}
            actions={actions}
          />

          <Card flat>
            <CardHeader>
              <CardTitle>{cardTitle}</CardTitle>
            </CardHeader>
            <CardBody>
              <InfoGrid items={items} />
            </CardBody>
          </Card>

          {children}
        </div>
      )}
    </Container>
  );
}
